import { reducerWithInitialState } from "typescript-fsa-reducers";
import { RangeType } from "../../components/Inputs/RangeInput";
import { SortDirection } from "../../generate";
import { booleanArrayToCount } from "../../utils/booleanArrayToCount";
import { nullToUndefined } from "../../utils/typeUtils";
import { updateSharedFilters } from "../payerLandscape/actions";
import { clearAdvancedFilters, updateFilters, updateSort } from "./actions";
import { FiltersWithRangeTypes, HcpLandscapeState } from "./types";

const filters = {
  genericFilter: "",
  region: "",
  timePeriod: "",
  targetList: "",
};

const advancedFilters = {
  name: "",
  marketTrxMin: undefined,
  marketTrxMax: undefined,
  marketRangeType: RangeType.GE,
  trxMin: undefined,
  trxMax: undefined,
  trxRangeType: RangeType.GE,
  favorableAccessPercentMin: undefined,
  favorableAccessPercentMax: undefined,
  favorableAccessPercentRangeType: RangeType.GE,
  favorableAccessChangePercentMin: undefined,
  favorableAccessChangePercentMax: undefined,
  favorableAccessChangePercentRangeType: RangeType.GE,
  overallAccessPercentMin: undefined,
  overallAccessPercentMax: undefined,
  overallAccessPercentRangeType: RangeType.GE,
  compTrxMin: undefined,
  compTrxMax: undefined,
  compTrxRangeType: RangeType.GE,
  productShareMin: undefined,
  productShareMax: undefined,
  productShareRangeType: RangeType.GE,
};

const allFilters: FiltersWithRangeTypes = { ...filters, ...advancedFilters };

// Metric default: highest market volume first, used when the mkt_trx column is visible.
export const defaultSort: HcpLandscapeState["sort"] = { field: "mkt_trx", direction: SortDirection.Descending };
// Fallback when mkt_trx is hidden (e.g. FRM views, slimmed configs) so we never sort by an unseen column.
export const fallbackSort: HcpLandscapeState["sort"] = { field: "full_name", direction: SortDirection.Ascending };

/**
 * Resolves the landscape sort: a product's explicit override wins; otherwise the metric
 * default is used only when mkt_trx is actually visible, else the alphabetical fallback.
 */
export const resolveLandscapeSort = (
  sortColumn: string | undefined,
  sortDirection: string | undefined,
  isMktTrxVisible: boolean,
): HcpLandscapeState["sort"] => {
  if (sortColumn) {
    return { field: sortColumn, direction: (sortDirection as SortDirection) || SortDirection.Ascending };
  }
  return isMktTrxVisible ? defaultSort : fallbackSort;
};

export const initialState: HcpLandscapeState = {
  filters: allFilters,
  advancedFilterCount: 0,
  sort: defaultSort,
};

const getAdvancedFilterCount = (f: FiltersWithRangeTypes) => {
  return booleanArrayToCount([
    f.name,
    f.marketTrxMin || f.marketTrxMax,
    f.trxMin || f.trxMax,
    f.compTrxMin || f.compTrxMax,
    f.favorableAccessPercentMin || f.favorableAccessPercentMax,
    f.favorableAccessChangePercentMin || f.favorableAccessChangePercentMax,
    f.overallAccessPercentMin || f.overallAccessPercentMax,
    f.productShareMin || f.productShareMax,
  ]);
};

const reducer = reducerWithInitialState(initialState)
  .case(updateSharedFilters, (state, { timePeriod }) => ({
    ...state,
    filters: { ...state.filters, timePeriod: nullToUndefined(timePeriod) },
    advancedFilterCount: getAdvancedFilterCount({ ...state.filters, timePeriod: nullToUndefined(timePeriod) }),
  }))
  .case(updateFilters, (state, filters) => ({
    ...state,
    filters: { ...state.filters, ...filters },
    advancedFilterCount: getAdvancedFilterCount({ ...state.filters, ...filters }),
  }))
  .case(clearAdvancedFilters, (state) => {
    //Clear filters but reserve the rangeTypes
    return {
      ...state,
      filters: {
        ...state.filters,
        ...advancedFilters,
        targetList: "",
        marketRangeType: state.filters.marketRangeType,
        trxRangeType: state.filters.trxRangeType,
        favorableAccessPercentRangeType: state.filters.favorableAccessPercentRangeType,
        favorableAccessChangePercentRangeType: state.filters.favorableAccessChangePercentRangeType,
        overallAccessPercentRangeType: state.filters.overallAccessPercentRangeType,
        compTrxRangeType: state.filters.compTrxRangeType,
        productShareRangeType: state.filters.productShareRangeType,
      },
      advancedFilterCount: 0,
    };
  })
  .case(updateSort, (state, sort) => {
    return { ...state, sort };
  });

export default reducer;
